// React functions
import React, { useState, ReactElement } from 'react'

// React UI
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Zoom from '@mui/material/Zoom'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'

// Icons
import PersonIcon from '@mui/icons-material/Person'
import EmailIcon from '@mui/icons-material/Email'
import CheckIcon from '@mui/icons-material/Check'
import LockIcon from '@mui/icons-material/Lock'
import KeyIcon from '@mui/icons-material/Key'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import themeColors from '@/assets/theme/colors'
import { AutoCompleteType, TooltipPlacement } from '@/types/global_types'
import { getFontBody, getFontBodySmall } from '@/assets/theme/fonts'

type IconList = 'person' | 'email' | 'check' | 'lock' | 'key'

type Props = {
  preventCopy?: boolean
  preventPaste?: boolean
  alignment?: 'horizontal' | 'vertical'
  icon?: IconList
  validation?: null | number
  title?: string | ReactElement
  titlePosition?: 'inside' | 'above'
  titleFontSize?: number | string
  inputFontSize?: number | string
  tooltip?: string | ReactElement
  tooltipPlacement?: TooltipPlacement
  width?: number
  height?: number
  marginBottom?: number | string
  marginTop?: number | string
  marginRight?: number | string
  marginLeft?: number | string
  iconColor?: string
  color?: string
  placeholder?: string
  disabled?: boolean
  inputMarginLeft?: number | string
  inputMarginTop?: number | string
  borderRadius?: number | string
  titleWidth?: number | string
  titleAlignment?: 'left' | 'right'
  type?: 'text' | 'number' | 'password'
  customStyle?: 'standard' | 'outlined'
  value: any
  multiline?: boolean
  /** Height the box starts at, in rows. It grows past this as you type. */
  minRows?: number
  rows?: number
  maxRows?: number
  autoCompleteType?: AutoCompleteType
  showPassword?: boolean // only for type=password
  onEnterKeyDown?: () => void
  onClick?: () => void
  onBlur?: () => void
  onChange: (val: string) => void
}

export default function FormTextInput(props: Props) {
  const [inputFocused, setInputFocused] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const getIcon = () => {
    switch (props.icon) {
      case 'person':
        return <PersonIcon />
      case 'email':
        return <EmailIcon />
      case 'check':
        return <CheckIcon />
      case 'lock':
        return <LockIcon />
      case 'key':
        return <KeyIcon />
    }
  }

  const getBorderColor = () => {
    if (props.validation === 1) return themeColors.success.darker
    if (props.validation === -1) return themeColors.error.main
    if (inputFocused) return '#5a6dcd'
    return '#ddd'
  }

  const showPasswordIcon = () => {
    if (props.showPassword === true)
      return showPassword ? <VisibilityOff /> : <Visibility />
    return null
  }

  return (
    <Box
      sx={{
        display: props.alignment === 'horizontal' ? 'flex' : 'block',
        alignItems: 'center',
        marginBottom: props.marginBottom ?? 0,
        marginTop: props.marginTop ?? 0,
        marginRight: props.marginRight ?? 0,
        marginLeft: props.marginLeft ?? 0,
        width: props.width ?? '100%'
      }}
    >
      {/* Title above field */}
      {props.title && (props.titlePosition ?? 'above') === 'above' && (
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: props.titleFontSize ?? getFontBodySmall(),
            marginBottom: 0.5
          }}
        >
          {props.title}
        </Typography>
      )}

      <Tooltip
        TransitionComponent={Zoom}
        arrow
        title={
          props.tooltip ? (
            <div style={{ fontSize: getFontBodySmall() }}>{props.tooltip}</div>
          ) : (
            ''
          )
        }
        placement={props.tooltipPlacement ?? 'right'}
        enterDelay={300}
      >
        <TextField
          fullWidth
          size="small"
          name={props.autoCompleteType}
          // Only an "inside" title becomes the floating label. Anything else
          // is already rendered above the field, and passing it here too
          // would draw the caption twice.
          label={props.titlePosition === 'inside' ? props.title : undefined}
          placeholder={props.placeholder}
          disabled={props.disabled}
          multiline={props.multiline}
          // minRows rather than rows, so the box grows with the content.
          minRows={
            props.multiline ? (props.minRows ?? props.rows ?? 4) : undefined
          }
          maxRows={props.maxRows}
          value={props.value}
          onChange={e => props.onChange(e.target.value)}
          onBlur={props.onBlur}
          onClick={props.onClick}
          onKeyDown={e => {
            if (e.key === 'Enter' && props.onEnterKeyDown) {
              props.onEnterKeyDown()
            }
          }}
          type={
            props.type === 'password'
              ? showPassword
                ? 'text'
                : 'password'
              : props.type
          }
          InputLabelProps={{
            sx: {
              fontSize: props.titleFontSize ?? getFontBodySmall()
            }
          }}
          autoComplete={props.autoCompleteType}
          InputProps={{
            autoComplete: props.autoCompleteType,
            startAdornment: props.icon ? (
              <InputAdornment position="start">{getIcon()}</InputAdornment>
            ) : null,
            endAdornment:
              props.type === 'password' ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    size="small"
                  >
                    {showPasswordIcon()}
                  </IconButton>
                </InputAdornment>
              ) : null
          }}
          sx={{
            background: '#fff',
            '& .MuiOutlinedInput-root': {
              fontSize: props.inputFontSize ?? getFontBodySmall(),
              // A given height acts as a floor, not a cap, so the field can
              // still grow when it is multiline.
              minHeight: props.height,
              alignItems: props.multiline ? 'flex-start' : 'center',
              '& fieldset': { borderColor: getBorderColor() },
              '&:hover fieldset': { borderColor: getBorderColor() },
              '&.Mui-focused fieldset': { borderColor: getBorderColor() }
            },
            // Lighter than MUI's default so the hint never reads as a value
            // the user has already typed.
            '& .MuiOutlinedInput-input::placeholder': {
              color: themeColors.gray.light,
              opacity: 1
            }
          }}
        />
      </Tooltip>
    </Box>
  )
}
